import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, ShoppingCartIcon, Upload, BarChart3, Menu } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Ticket, HandHelping, Briefcase,
  CircleUserRound, LayoutTemplate, Package, Mail, LogOut, X,
} from 'lucide-react';

const MobileBottomNav = ({ onLogout }) => {
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  const mainTabs = [
    { icon: HomeIcon, label: 'Store', to: '/homepage' },
    { icon: ShoppingCartIcon, label: 'Orders', to: '/Orders' },
    { icon: Upload, label: 'Upload', to: '/UploadProduct' },
    { icon: BarChart3, label: 'Analytics', to: '/analytics' },
  ];

  const moreItems = [
    { icon: Globe, label: 'SEO Settings', to: '/seo-settings' },
    { icon: Ticket, label: 'My Coupons', to: '/my-coupons' },
    { icon: HandHelping, label: 'Bid Requests', to: '/Proposals' },
    { icon: Briefcase, label: 'Designer KYC', to: '/designer-onboarding' },
    { icon: CircleUserRound, label: 'Brand Profile', to: '/Profile' },
    { icon: Briefcase, label: 'Designer Profile', to: '/designer-profile' },
    { icon: LayoutTemplate, label: 'Themes', to: '/TemplateThemes' },
    { icon: Package, label: 'Subscription', to: '/subscription' },
    { icon: Mail, label: 'Contact Us', to: '/Contact' },
  ];

  const isActive = (to) => location.pathname === to;
  const isMoreActive = moreItems.some(item => location.pathname === item.to);

  return (
    <>
      {/* More Menu Bottom Sheet */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 z-[9998]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMore(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-[20px] shadow-2xl"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">More</h3>
                <button
                  onClick={() => setShowMore(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Grid of items */}
              <div className="grid grid-cols-3 gap-1 p-4 max-h-[60vh] overflow-y-auto">
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setShowMore(false)}
                      className={`flex flex-col items-center gap-2 py-4 px-2 rounded-2xl transition-all active:scale-95 ${
                        active
                          ? 'bg-orange-50 text-[#d45614]'
                          : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                      }`}
                    >
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                        active ? 'bg-[#d45614] text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className={`text-[11px] font-medium text-center leading-tight ${
                        active ? 'text-[#d45614]' : 'text-gray-600'
                      }`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>

              {/* Logout */}
              <div className="px-4 pb-6 pt-2 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowMore(false);
                    if (onLogout) onLogout();
                  }}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-red-500 bg-red-50 active:bg-red-100 font-medium text-sm transition-all"
                >
                  <LogOut className="w-4.5 h-4.5" />
                  Log out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9997] bg-white border-t border-gray-200 lg:hidden safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-1">
          {mainTabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className="flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform"
              >
                {active && (
                  <motion.div
                    layoutId="bottomNavIndicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-[#d45614] rounded-b-full"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${
                  active ? 'bg-orange-50' : ''
                }`}>
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors ${
                      active ? 'text-[#d45614]' : 'text-gray-400'
                    }`}
                    strokeWidth={active ? 2.2 : 1.8}
                  />
                </div>
                <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
                  active ? 'text-[#d45614]' : 'text-gray-400'
                }`}>
                  {tab.label}
                </span>
              </Link>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setShowMore(true)}
            className="flex flex-col items-center justify-center flex-1 h-full relative active:scale-95 transition-transform"
          >
            {isMoreActive && (
              <motion.div
                className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] bg-[#d45614] rounded-b-full"
              />
            )}
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${
              isMoreActive ? 'bg-orange-50' : ''
            }`}>
              <Menu
                className={`w-[22px] h-[22px] transition-colors ${
                  isMoreActive ? 'text-[#d45614]' : 'text-gray-400'
                }`}
                strokeWidth={isMoreActive ? 2.2 : 1.8}
              />
            </div>
            <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
              isMoreActive ? 'text-[#d45614]' : 'text-gray-400'
            }`}>
              More
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
